/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Auth.css';
import AuthBrandHeader from './AuthBrandHeader';
import { useBranding } from '../../contexts/BrandingContext';

// Emails a temporary password (see backend POST /api/auth/forgot-password).
// The reply is the same whether or not the account exists.
const ForgotPassword = () => {
  const { branding } = useBranding();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sentMessage, setSentMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      setSentMessage(response.data?.message || 'Check your email for sign-in instructions.');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <AuthBrandHeader
        heading={branding.signin_heading || 'Plant Controlled Environment Laboratory Services Login'}
        subheading={branding.signin_subheading}
      />

      <div className="auth-container">
        <h2>Forgot password</h2>
        {sentMessage ? (
          <>
            <div className="info-message">{sentMessage}</div>
            <p className="otp-hint">
              The email contains a temporary password that works for 24 hours. Sign in with it, and
              you'll be taken to your profile to choose a new password.
            </p>
            <div className="auth-link">
              <p><Link to="/signin">Back to sign in</Link></p>
              <p>
                Didn't get it?{' '}
                <button type="button" className="link-button" onClick={() => setSentMessage('')}>
                  Try again
                </button>
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="otp-hint">
              Enter the email address you signed up with and we'll email you a temporary password.
            </p>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <button type="submit" className="auth-button" disabled={submitting}>
                {submitting ? 'Sending...' : 'Email me a temporary password'}
              </button>
            </form>
            <div className="auth-link">
              <p><Link to="/signin">Back to sign in</Link></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
